declare global {
    interface Window {
        heap: any;
        heapReadyCb: Array<{ name: string; fn: () => void }>;
    }
}

function handleHeapCookies(appid: string) {

    const getRootDomain = function () {
        let rootDomain = '.spglobal.com;';
        const domain = window.location.host.split('.').reverse();

        if (domain.length >= 2) {
            rootDomain = `.${domain[1]}.${domain[0]};`;
        }

        return rootDomain;
    };
    const deleteCookie = (name: string) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${getRootDomain()}`;
    };

    const browserCookies = document.cookie.split(';').reduce((cookies: any, cookie) => { const [name, value] = cookie.split('=').map(c => c.trim()); cookies[name] = value; return cookies; }, {});
    const cookieNames = Object.keys(browserCookies);

    cookieNames.forEach(cookieName => {
        if (cookieName.indexOf('_hp5') === 0) {
            if (cookieName.indexOf(appid) === -1) {
                deleteCookie(cookieName);
            }
        }
    });
}

function initializeHeap(envId: string, config: Record<string, any> = {}) {
    handleHeapCookies(envId);
    if (window.heap && window.heap.loaded) {
        return window.heap;
    }
    window.heapReadyCb = window.heapReadyCb || [];
    window.heap = window.heap || [];

    window.heap.load = function (e: string, t: Record<string, any> = {}) {
        window.heap.envId = e;
        window.heap.clientConfig = { ...t, secureCookie: true }; // <-- Add this line
        window.heap.clientConfig.shouldFetchServerConfig = false;

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://cdn.us.heap-api.com/config/${e}/heap_config.js`;

        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        }

        const methods = [
            'init',
            'startTracking',
            'stopTracking',
            'track',
            'resetIdentity',
            'identify',
            'getSessionId',
            'getUserId',
            'getIdentity',
            'addUserProperties',
            'addEventProperties',
            'removeEventProperty',
            'clearEventProperties',
            'addAccountProperties',
            'addAdapter',
            'addTransformer',
            'addTransformerFn',
            'onReady',
            'addPageviewProperties',
            'removePageviewProperty',
            'clearPageviewProperties',
            'trackPageview'
        ];

        const factory = (method: string) => {
            return (...args: any[]) => {
                window.heapReadyCb.push({
                    name: method,
                    fn: () => {
                        if (window.heap[method]) {
                            window.heap[method](...args);
                        }
                    },
                });
            };
        };

        methods.forEach(method => {
            window.heap[method] = factory(method);
        });
    };

    window.heap.load(envId, config);
    return window.heap;
}

function disableHeapTextCapture(form: HTMLFormElement) {
    if (window.heap && typeof window.heap.disableTextCapture === 'function') {
        window.heap.disableTextCapture(form);
    }
}

function addUserData(keyonlineuser: string, subscriberAccount: string) {
    if (window.heap && typeof window.heap.addUserProperties === 'function') {
        window.heap.identify(keyonlineuser);
        window.heap.addUserProperties({
            SubscriberAccount: subscriberAccount
        });
    }
}

function clearUserData() {
    if (window.heap) {
        if (typeof window.heap.addUserProperties === 'function') {
            window.heap.addUserProperties({});
        }
        if (typeof window.heap.resetIdentity === 'function') {
            window.heap.resetIdentity();
        }
    }
}

async function trackData(eventName: string, data: Record<string, any> = {}) {
    if (eventName) {
        if (window.heap && typeof window.heap.track === 'function') {
            window.heap.trackPageview();
            await window.heap.track(eventName, data);
            const isCountableEvent = eventName === 'Keyword Search' || eventName === 'Filter Applied';
            if (
                isCountableEvent &&
                Number(data.all_results_count) === 0 &&
                (data.search_term || data.search_location)
            ) {
                await window.heap.track('zero_results_search', {
                    search_term: data.search_term || '',
                    search_location: data.search_location || '',
                    trigger_method: data.trigger_method || '',
                    all_results_count: 0,
                    datasets_count: Number(data.datasets_count) || 0,
                    solutions_count: Number(data.solutions_count) || 0,
                    blueprints_count: Number(data.blueprints_count) || 0
                });
            }
        } else {
            // Retry after short delay
            // Check OneTrust cookie consent
            const oneTrustCookies = window.OnetrustActiveGroups || '';
            const perfCookiesEnabled = oneTrustCookies.includes('C0002');
            // const allRejected = oneTrustCookies === '';
            const groups = oneTrustCookies.split(',').filter(Boolean);
            const allRejected = groups.length === 1 && groups[0] === 'C0001';

            if (!allRejected && perfCookiesEnabled) {
                console.log('Heap not ready yet');
                setTimeout(() => trackData(eventName, data), 100);
            }
        }
    }
}

// Module-scoped debounce handle (lint-safe)
let searchInputDebounceTimer: number | undefined;

function trackSearchInput(eventName: string, props: Record<string, any> = {}) {
    try {
        if (searchInputDebounceTimer) {
            clearTimeout(searchInputDebounceTimer);
        }
        searchInputDebounceTimer = window.setTimeout(() => {
            trackData(eventName, props);
        }, 600); // debounce 600ms
    } catch {
        /* no-op */
    }
}

const getSearchLocation = () => {
    const trimmed = window.location.pathname.replace(/\/+$/, ''); // drop trailing slash
    const parts = trimmed.split('/').filter(Boolean);
    return `/${parts.pop() || 'root'}`;
};

type RelatedLink = {
    link_category: string;
    name: string;
    url: string;
};

const getLLMRelatedLinks = (relatedLinks: RelatedLink[], userType: string, page = '') => {
    if (relatedLinks && relatedLinks.length > 0) {

        const actions: RelatedLink[] = []; // callToAction buttons objects
        const filteredLinks: RelatedLink[] = relatedLinks.filter((link: any) => link.url !== undefined && link.link_category !== undefined); // valid links
        // get call-to-action links according to relevancy scores
        // filteredLinks are already ordered acoording to relevancy
        // we just need to get first object from each category and not loose order
        if (userType === 'not_authenticated') {
            const nonLoggedInActions: RelatedLink[] = [];
            filteredLinks.forEach((link: RelatedLink) => {
                if (link.link_category === 'faq' && page !== 'profile' && !nonLoggedInActions.some(el => el.link_category === 'faq')) {
                    nonLoggedInActions.push(link);
                }
            });
            return nonLoggedInActions.map(l => l.url).filter(Boolean).join(' | ');
        }

        filteredLinks.forEach((link: any) => {
            const cat = link.link_category;
            if ((cat === 'tile_user_guide' || cat === 'data_guide' || cat === 'knowledge_base' || (cat === 'faq' && page !== 'profile')) && !actions.some(el => el.link_category === cat)) {
                actions.push(link);
            }
        });
        return actions.map(l => l.url).filter(Boolean).join(' | ');
    }
    return '';
};

const filterSubCategory = (filterValue: string) => {
    try {
        const filterObj = typeof filterValue === 'string' ? JSON.parse(filterValue) : filterValue;
        return Object.values(filterObj).flat().join(',');
    } catch {
        return '';
    }
};

const filterCategory = (filterValue: string) => {
    try {
        const filterObj = typeof filterValue === 'string' ? JSON.parse(filterValue) : filterValue;
        // console.log('cat: ', Object.keys(filterObj).join(','));
        return Object.keys(filterObj).join(',');
    } catch {
        return '';
    }
};

export default {
    initializeHeap,
    disableHeapTextCapture,
    addUserData,
    clearUserData,
    getSearchLocation,
    trackSearchInput,
    getLLMRelatedLinks,
    filterSubCategory,
    filterCategory,
    trackData
};
